import {
    GeneratorType,
    GENERATOR_TYPES,
    SOLAR_TYPE,
    STORAGE_TYPE,
    WIND_TYPE
} from "./generatorType";
import { RegionType } from "./regionType";

export const PJM_SOLAR_SCOPE_VIEW = "pjm_solar";
export const PJM_LOAD_SCOPE_VIEW = "pjm_load";
export const PJM_WIND_SCOPE_VIEW = "pjm_wind";
export const PJM_STORAGE_SCOPE_VIEW = "pjm_storage";

export const MISO_SOLAR_SCOPE_VIEW_TYPE = "miso_solar";
export const MISO_WIND_SCOPE_VIEW_TYPE = "miso_wind";
export const MISO_STORAGE_SCOPE_VIEW_TYPE = "miso_storage";
export const MISO_LOAD_SCOPE_VIEW_TYPE = "miso_load";

export const SPP_SOLAR_SCOPE_VIEW_TYPE = "spp_solar";
export const SPP_WIND_SCOPE_VIEW_TYPE = "spp_wind";
export const SPP_LOAD_SCOPE_VIEW_TYPE = "spp_load";
// SPP generation now uses the CPP ICS scope views; the legacy spp_solar/spp_wind
// views (DISIS-based scopes) are kept as known types for backwards
// compatibility but are no longer surfaced.
export const SPP_CPP_ICS_SOLAR_SCOPE_VIEW_TYPE = "spp_cpp_ics_solar";
export const SPP_CPP_ICS_WIND_SCOPE_VIEW_TYPE = "spp_cpp_ics_wind";

export const WECC_DISCHARGING_SCOPE_VIEW_TYPE = "wecc_discharging";
export const WECC_STORAGE_SCOPE_VIEW_TYPE = "wecc_storage";
export const WECC_LOAD_SCOPE_VIEW_TYPE = "wecc_load";

export const TVA_SOLAR_SCOPE_VIEW_TYPE = "tva_solar";
export const TVA_DISCHARGING_SCOPE_VIEW_TYPE = "tva_eris_nris_discharging";
export const TVA_CHARGING_SCOPE_VIEW_TYPE = "tva_eris_nris_charging";
export const TVA_STORAGE_SCOPE_VIEW_TYPE = "tva_eris_nris_storage";

export const SOCO_SOLAR_SCOPE_VIEW_TYPE = "soco_solar";
export const SOCO_LOAD_SCOPE_VIEW_TYPE = "soco_load";

export const ISONE_DISCHARGING_AND_CHARGING_SCOPE_VIEW =
    "isone_discharging_and_charging";
export const ISONE_DISCHARGING_SCOPE_VIEW = "isone_discharging";

export const ERCOT_SP_DISCHARGING_HRML_CHARGING_SCOPE_VIEW =
    "sp_discharging_hrml_charging";
export const ERCOT_SP_CHARGING_SCOPE_VIEW = "sp_charging";
export const ERCOT_HRML_DISCHARGING_SCOPE_VIEW = "hrml_discharging";
export const ERCOT_SP_LOAD_SCOPE_VIEW = "sp_ercot_load";

export const NYISO_SP_DISCHARGING_LL_CHARGING_SCOPE_VIEW =
    "nyiso_sp_discharging_ll_charging";

export const DUKE_SOLAR_SCOPE_VIEW_TYPE = "duke_solar";
export const DUKE_STORAGE_SCOPE_VIEW_TYPE = "duke_storage";
export const DUKE_LOAD_SCOPE_VIEW_TYPE = "duke_load";

export const CAISO_SOLAR_SCOPE_VIEW = "caiso_solar";
export const CAISO_WIND_SCOPE_VIEW = "caiso_wind";
export const CAISO_STORAGE_SCOPE_VIEW = "caiso_storage";

export const SCOPE_VIEW_TYPES = [
    PJM_SOLAR_SCOPE_VIEW,
    PJM_LOAD_SCOPE_VIEW,
    PJM_WIND_SCOPE_VIEW,
    PJM_STORAGE_SCOPE_VIEW,
    MISO_SOLAR_SCOPE_VIEW_TYPE,
    MISO_WIND_SCOPE_VIEW_TYPE,
    MISO_STORAGE_SCOPE_VIEW_TYPE,
    MISO_LOAD_SCOPE_VIEW_TYPE,
    SPP_SOLAR_SCOPE_VIEW_TYPE,
    SPP_WIND_SCOPE_VIEW_TYPE,
    SPP_LOAD_SCOPE_VIEW_TYPE,
    SPP_CPP_ICS_SOLAR_SCOPE_VIEW_TYPE,
    SPP_CPP_ICS_WIND_SCOPE_VIEW_TYPE,
    WECC_DISCHARGING_SCOPE_VIEW_TYPE,
    WECC_STORAGE_SCOPE_VIEW_TYPE,
    WECC_LOAD_SCOPE_VIEW_TYPE,
    TVA_SOLAR_SCOPE_VIEW_TYPE,
    TVA_DISCHARGING_SCOPE_VIEW_TYPE,
    TVA_CHARGING_SCOPE_VIEW_TYPE,
    TVA_STORAGE_SCOPE_VIEW_TYPE,
    SOCO_SOLAR_SCOPE_VIEW_TYPE,
    SOCO_LOAD_SCOPE_VIEW_TYPE,
    ISONE_DISCHARGING_AND_CHARGING_SCOPE_VIEW,
    ISONE_DISCHARGING_SCOPE_VIEW,
    ERCOT_SP_DISCHARGING_HRML_CHARGING_SCOPE_VIEW,
    ERCOT_SP_CHARGING_SCOPE_VIEW,
    ERCOT_HRML_DISCHARGING_SCOPE_VIEW,
    ERCOT_SP_LOAD_SCOPE_VIEW,
    NYISO_SP_DISCHARGING_LL_CHARGING_SCOPE_VIEW,
    DUKE_SOLAR_SCOPE_VIEW_TYPE,
    DUKE_STORAGE_SCOPE_VIEW_TYPE,
    DUKE_LOAD_SCOPE_VIEW_TYPE,
    CAISO_SOLAR_SCOPE_VIEW,
    CAISO_WIND_SCOPE_VIEW,
    CAISO_STORAGE_SCOPE_VIEW
] as const;
export type ScopeViewType = (typeof SCOPE_VIEW_TYPES)[number];

export type ScopeViewConfig = {
    capacityCostScopeView: ScopeViewType;
    constraintStackScopeViews: readonly ScopeViewType[];
};

const REGION_TO_FUEL_TYPE_TO_SCOPE_VIEW: {
    [key in RegionType]: {
        [key in GeneratorType]: ScopeViewConfig | undefined;
    };
} = {
    NYISO: {
        solar: {
            capacityCostScopeView: NYISO_SP_DISCHARGING_LL_CHARGING_SCOPE_VIEW,
            constraintStackScopeViews: [
                NYISO_SP_DISCHARGING_LL_CHARGING_SCOPE_VIEW
            ]
        },
        storage: {
            capacityCostScopeView: NYISO_SP_DISCHARGING_LL_CHARGING_SCOPE_VIEW,
            constraintStackScopeViews: [
                NYISO_SP_DISCHARGING_LL_CHARGING_SCOPE_VIEW
            ]
        },
        wind: undefined,
        load: undefined
    },
    PJM: {
        solar: {
            capacityCostScopeView: PJM_SOLAR_SCOPE_VIEW,
            constraintStackScopeViews: [PJM_SOLAR_SCOPE_VIEW]
        },
        storage: {
            capacityCostScopeView: PJM_STORAGE_SCOPE_VIEW,
            constraintStackScopeViews: [PJM_STORAGE_SCOPE_VIEW]
        },
        wind: {
            capacityCostScopeView: PJM_WIND_SCOPE_VIEW,
            constraintStackScopeViews: [PJM_WIND_SCOPE_VIEW]
        },
        load: {
            capacityCostScopeView: PJM_LOAD_SCOPE_VIEW,
            constraintStackScopeViews: [PJM_LOAD_SCOPE_VIEW]
        }
    },
    MISO: {
        solar: {
            capacityCostScopeView: MISO_SOLAR_SCOPE_VIEW_TYPE,
            constraintStackScopeViews: [MISO_SOLAR_SCOPE_VIEW_TYPE]
        },
        storage: {
            capacityCostScopeView: MISO_STORAGE_SCOPE_VIEW_TYPE,
            constraintStackScopeViews: [MISO_STORAGE_SCOPE_VIEW_TYPE]
        },
        wind: {
            capacityCostScopeView: MISO_WIND_SCOPE_VIEW_TYPE,
            constraintStackScopeViews: [MISO_WIND_SCOPE_VIEW_TYPE]
        },
        load: {
            capacityCostScopeView: MISO_LOAD_SCOPE_VIEW_TYPE,
            constraintStackScopeViews: [MISO_LOAD_SCOPE_VIEW_TYPE]
        }
    },
    CAISO: {
        solar: {
            capacityCostScopeView: CAISO_SOLAR_SCOPE_VIEW,
            constraintStackScopeViews: [CAISO_SOLAR_SCOPE_VIEW]
        },
        storage: {
            capacityCostScopeView: CAISO_STORAGE_SCOPE_VIEW,
            constraintStackScopeViews: [CAISO_STORAGE_SCOPE_VIEW]
        },
        wind: {
            capacityCostScopeView: CAISO_WIND_SCOPE_VIEW,
            constraintStackScopeViews: [CAISO_WIND_SCOPE_VIEW]
        },
        load: undefined
    },
    SPP: {
        solar: {
            capacityCostScopeView: SPP_CPP_ICS_SOLAR_SCOPE_VIEW_TYPE,
            constraintStackScopeViews: [SPP_CPP_ICS_SOLAR_SCOPE_VIEW_TYPE]
        },
        storage: undefined,
        wind: {
            capacityCostScopeView: SPP_CPP_ICS_WIND_SCOPE_VIEW_TYPE,
            constraintStackScopeViews: [SPP_CPP_ICS_WIND_SCOPE_VIEW_TYPE]
        },
        load: {
            capacityCostScopeView: SPP_LOAD_SCOPE_VIEW_TYPE,
            constraintStackScopeViews: [SPP_LOAD_SCOPE_VIEW_TYPE]
        }
    },
    ERCOT: {
        solar: {
            capacityCostScopeView:
                ERCOT_SP_DISCHARGING_HRML_CHARGING_SCOPE_VIEW,
            constraintStackScopeViews: [
                ERCOT_SP_DISCHARGING_HRML_CHARGING_SCOPE_VIEW,
                ERCOT_HRML_DISCHARGING_SCOPE_VIEW
            ]
        },
        storage: {
            capacityCostScopeView:
                ERCOT_SP_DISCHARGING_HRML_CHARGING_SCOPE_VIEW,
            constraintStackScopeViews: [
                ERCOT_SP_DISCHARGING_HRML_CHARGING_SCOPE_VIEW,
                ERCOT_HRML_DISCHARGING_SCOPE_VIEW,
                ERCOT_SP_CHARGING_SCOPE_VIEW
            ]
        },
        wind: {
            capacityCostScopeView: ERCOT_HRML_DISCHARGING_SCOPE_VIEW,
            constraintStackScopeViews: [
                ERCOT_SP_DISCHARGING_HRML_CHARGING_SCOPE_VIEW,
                ERCOT_HRML_DISCHARGING_SCOPE_VIEW
            ]
        },
        load: {
            capacityCostScopeView: ERCOT_SP_LOAD_SCOPE_VIEW,
            constraintStackScopeViews: [ERCOT_SP_LOAD_SCOPE_VIEW]
        }
    },
    WECC: {
        solar: {
            capacityCostScopeView: WECC_DISCHARGING_SCOPE_VIEW_TYPE,
            constraintStackScopeViews: [WECC_DISCHARGING_SCOPE_VIEW_TYPE]
        },
        storage: {
            capacityCostScopeView: WECC_STORAGE_SCOPE_VIEW_TYPE,
            constraintStackScopeViews: [WECC_STORAGE_SCOPE_VIEW_TYPE]
        },
        wind: undefined,
        load: {
            capacityCostScopeView: WECC_LOAD_SCOPE_VIEW_TYPE,
            constraintStackScopeViews: [WECC_LOAD_SCOPE_VIEW_TYPE]
        }
    },
    TVA: {
        solar: {
            capacityCostScopeView: TVA_DISCHARGING_SCOPE_VIEW_TYPE,
            constraintStackScopeViews: [TVA_DISCHARGING_SCOPE_VIEW_TYPE]
        },
        storage: {
            capacityCostScopeView: TVA_STORAGE_SCOPE_VIEW_TYPE,
            constraintStackScopeViews: [
                TVA_DISCHARGING_SCOPE_VIEW_TYPE,
                TVA_CHARGING_SCOPE_VIEW_TYPE,
                TVA_STORAGE_SCOPE_VIEW_TYPE
            ]
        },
        wind: undefined,
        load: undefined
    },
    ISONE: {
        solar: {
            capacityCostScopeView: ISONE_DISCHARGING_SCOPE_VIEW,
            constraintStackScopeViews: [ISONE_DISCHARGING_SCOPE_VIEW]
        },
        storage: {
            capacityCostScopeView: ISONE_DISCHARGING_AND_CHARGING_SCOPE_VIEW,
            constraintStackScopeViews: [
                ISONE_DISCHARGING_AND_CHARGING_SCOPE_VIEW
            ]
        },
        wind: undefined,
        load: undefined
    },
    SOCO: {
        solar: {
            capacityCostScopeView: SOCO_SOLAR_SCOPE_VIEW_TYPE,
            constraintStackScopeViews: [SOCO_SOLAR_SCOPE_VIEW_TYPE]
        },
        storage: undefined,
        wind: undefined,
        load: {
            capacityCostScopeView: SOCO_LOAD_SCOPE_VIEW_TYPE,
            constraintStackScopeViews: [SOCO_LOAD_SCOPE_VIEW_TYPE]
        }
    },
    DUKE: {
        solar: {
            capacityCostScopeView: DUKE_SOLAR_SCOPE_VIEW_TYPE,
            constraintStackScopeViews: [DUKE_SOLAR_SCOPE_VIEW_TYPE]
        },
        storage: {
            capacityCostScopeView: DUKE_STORAGE_SCOPE_VIEW_TYPE,
            constraintStackScopeViews: [DUKE_STORAGE_SCOPE_VIEW_TYPE]
        },
        wind: undefined,
        load: {
            capacityCostScopeView: DUKE_LOAD_SCOPE_VIEW_TYPE,
            constraintStackScopeViews: [DUKE_LOAD_SCOPE_VIEW_TYPE]
        }
    }
};

const maybeGetScopeViewConfigFromRegionAndGenerator = (
    region: RegionType,
    generatorType: GeneratorType
): ScopeViewConfig | undefined => {
    return REGION_TO_FUEL_TYPE_TO_SCOPE_VIEW[region][generatorType];
};

const getScopeViewConfigFromRegionAndGenerator = (
    region: RegionType,
    generatorType: GeneratorType
): ScopeViewConfig => {
    const config = maybeGetScopeViewConfigFromRegionAndGenerator(
        region,
        generatorType
    );
    if (!config) {
        throw new Error("Generator and region combination not supported");
    }
    return config;
};

export const maybeGetConstraintStackScopeViewsFromRegionAndGenerator = (
    region: RegionType,
    generatorType: GeneratorType
): readonly ScopeViewType[] | undefined => {
    return maybeGetScopeViewConfigFromRegionAndGenerator(region, generatorType)
        ?.constraintStackScopeViews;
};

export const maybeGetCapacityCostsScopeViewFromRegionAndGenerator = (
    region: RegionType,
    generatorType: GeneratorType
): ScopeViewType | undefined =>
    maybeGetScopeViewConfigFromRegionAndGenerator(region, generatorType)
        ?.capacityCostScopeView;

export const getAllCapacityCostsScopeViewsForRegion = (
    region: RegionType
): ReadonlyArray<ScopeViewType> =>
    Array.from(
        new Set(
            GENERATOR_TYPES.map((ft) =>
                maybeGetCapacityCostsScopeViewFromRegionAndGenerator(region, ft)
            ).filter((sv): sv is ScopeViewType => sv !== undefined)
        )
    );

// Generation (non-load) fuel types the region actually supports. Used to
// drive re-study fuel-type selection. MISO -> [solar, wind, storage];
// WECC -> [solar, storage] (WECC has no standalone wind type).
const GENERATION_FUEL_TYPES: ReadonlyArray<GeneratorType> = [
    SOLAR_TYPE,
    WIND_TYPE,
    STORAGE_TYPE
];

export const getGenerationFuelTypesForRegion = (
    region: RegionType
): ReadonlyArray<GeneratorType> =>
    GENERATION_FUEL_TYPES.filter(
        (ft) =>
            maybeGetScopeViewConfigFromRegionAndGenerator(region, ft) !==
            undefined
    );

export const maybeGetGenerationFuelType = (
    region: RegionType,
    generatorType: GeneratorType
): GeneratorType | undefined =>
    getGenerationFuelTypesForRegion(region).includes(generatorType)
        ? generatorType
        : undefined;

export const getCapacityCostsScopeViewFromRegionAndGenerator = (
    region: RegionType,
    generatorType: GeneratorType
): ScopeViewType => {
    const config = getScopeViewConfigFromRegionAndGenerator(
        region,
        generatorType
    );
    return config.capacityCostScopeView;
};

export const getConstraintStackScopeViewsFromRegionAndGenerator = (
    region: RegionType,
    generatorType: GeneratorType
): readonly ScopeViewType[] => {
    const config = getScopeViewConfigFromRegionAndGenerator(
        region,
        generatorType
    );
    return config.constraintStackScopeViews;
};

export const isKnownScopeViewType = (type: string): type is ScopeViewType => {
    return SCOPE_VIEW_TYPES.includes(type as ScopeViewType);
};

export const convertToScopeViewType = (string: string): ScopeViewType => {
    if (isKnownScopeViewType(string)) {
        return string;
    }

    throw new Error(`Unexpected scopeView type: "${string}"`);
};

export const ERCOT_SCOPE_VIEW_TYPE = [
    ERCOT_SP_DISCHARGING_HRML_CHARGING_SCOPE_VIEW,
    ERCOT_SP_CHARGING_SCOPE_VIEW,
    ERCOT_HRML_DISCHARGING_SCOPE_VIEW
] as const;
export type ErcotScopeViewType = (typeof ERCOT_SCOPE_VIEW_TYPE)[number];

export const isKnownErcotScopeViewType = (
    type: ScopeViewType
): type is ErcotScopeViewType => {
    return ERCOT_SCOPE_VIEW_TYPE.includes(type as ErcotScopeViewType);
};
